import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Mail, Instagram, MapPin, Home, Heart, Smartphone, Search, Building2, Map, Plus, HelpCircle, FileText, Shield, MessageCircle, Activity, CheckCircle, AlertTriangle, Wrench } from "lucide-react";
import healthService from "@/services/healthService";

const STATUS_ICON = {
  healthy: <CheckCircle className="w-3 h-3 text-sauna-green" />,
  degraded: <AlertTriangle className="w-3 h-3 text-sauna-orange" />,
  unhealthy: <Activity className="w-3 h-3 text-sauna-orange" />,
  maintenance: <Wrench className="w-3 h-3 text-sauna-blue" />,
  unknown: <Activity className="w-3 h-3 text-sauna-grey" />,
};
const STATUS_LABEL = {
  healthy: "All systems operational",
  degraded: "Degraded",
  unhealthy: "Major outage",
  maintenance: "Maintenance",
  unknown: "Unknown",
};

const FooterVariant: React.FC = () => {
  const [status, setStatus] = React.useState("unknown");

  React.useEffect(() => {
    // Only check system health in production, not in development
    if (import.meta.env.PROD) {
      healthService.checkSystemHealth().then((data) => {
        setStatus(data?.status || "unknown");
      });
    } else {
      // In development, just set a default status
      setStatus("operational");
    }
  }, []);

  return (
    <footer className="bg-sauna-navy text-white pt-12 pb-8 px-4">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-12 md:gap-20 justify-between">
        {/* Left: Branding, Tagline, Popular Searches */}
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center mb-4">
            <Logo size="sm" className="h-10 w-auto" />
          </div>
          <p className="text-sauna-beige/80 mb-6 max-w-xs">
            SaunaDip is Ireland's first complete guide to saunas and cold plunge spots — connecting you with wild wellness experiences across the country.
          </p>
          <div className="mb-6">
            <div className="font-semibold text-lg mb-2 flex items-center gap-2">
              <Search className="w-5 h-5" /> Popular Searches:
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sauna-beige/90">
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Barrel Saunas</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Wicklow</div>
              <div className="flex items-center gap-2"><Home className="w-4 h-4" /> Lakeside Saunas</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Galway</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Mobile Saunas</div>
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> West Cork</div>
            </div>
          </div>
          <p className="text-sauna-beige/70 text-sm mb-2">SaunaDip is independently built in Ireland by sauna lovers for sauna lovers.<br/>No data selling. No lock-in. You stay in control.</p>
          <div className="flex items-center gap-4 mt-4">
            <Link to="/contact" className="text-sauna-beige/80 hover:text-sauna-orange transition"><Mail size={20} /></Link>
            <a href="https://www.instagram.com/saunadip" target="_blank" rel="noopener noreferrer" className="text-sauna-beige/80 hover:text-sauna-orange transition"><Instagram size={20} /></a>
          </div>
        </div>

        {/* Right: Newsletter/CTA, App Teaser, Trust, Status */}
        <div className="flex-1 min-w-[260px] flex flex-col gap-8">
          <div>
            <h3 className="text-2xl font-bold mb-2">Stay in the Loop</h3>
            <p className="mb-4 text-sauna-beige/80">Get SaunaDip news and cold plunge tips.</p>
            <form className="flex flex-col sm:flex-row gap-2 mb-2">
              <input type="email" placeholder="Your email" className="rounded-lg px-4 py-2 text-sauna-navy bg-white/90 focus:ring-2 focus:ring-sauna-orange outline-none" />
              <button type="submit" className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-bold px-6 py-2 rounded-lg transition">Subscribe Now</button>
            </form>
            <div className="mt-6 space-y-2 text-sauna-beige/90 text-sm">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-sauna-orange" /> The SaunaDip App is launching soon!
              </div>
              <div className="ml-6 text-sauna-beige/80">Track dips. Discover heat. Anywhere.</div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-pink-400" /> Beta testing now open – join the first wave.
              </div>
              <div className="flex items-center gap-2 italic text-sauna-beige/80">
                <MessageCircle className="w-4 h-4" /> "The cold never felt so good." – A future guest, probably.
              </div>
              <div className="flex items-center gap-2">
                <Map className="w-4 h-4 text-sauna-blue" /> Have a sauna? <Link to="/choose-signup" className="underline hover:text-sauna-orange transition">Add it to the map</Link> and get early perks.
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Link to="/directory" className="text-center bg-sauna-blue hover:bg-sauna-blue/90 text-white font-medium px-4 py-2 rounded-lg transition text-sm">
                🔗 Suggest a Sauna
              </Link>
              <Link to="/choose-signup" className="text-center bg-sauna-green hover:bg-sauna-green/90 text-white font-medium px-4 py-2 rounded-lg transition text-sm">
                Join the Beta
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sauna-beige/70 text-xs mt-4">
            <Link to="/system-status" className="hover:text-sauna-orange transition flex items-center gap-1">
              {STATUS_ICON[status]}
              <span>{STATUS_LABEL[status]}</span>
            </Link>
            <span className="mx-2">|</span>
            <Link to="/changelog" className="hover:text-sauna-orange transition">Changelog</Link>
          </div>
        </div>
      </div>
      
      <div className="border-t border-sauna-beige/20 mt-12 pt-8">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-sauna-beige/70">
          <Link to="/terms-and-conditions" className="hover:text-sauna-orange transition flex items-center gap-1">
            <FileText className="w-3 h-3" /> Terms of Use
          </Link>
          <Link to="/privacy-policy" className="hover:text-sauna-orange transition flex items-center gap-1">
            <Shield className="w-3 h-3" /> Privacy Policy
          </Link>
          <Link to="/contact" className="hover:text-sauna-orange transition flex items-center gap-1">
            <MessageCircle className="w-3 h-3" /> Contact Us
          </Link>
          <a href="/become-operator" className="hover:text-sauna-orange transition flex items-center gap-1">
            <Plus className="w-3 h-3" /> Become an Operator
          </a>
          <Link to="/faq" className="hover:text-sauna-orange transition flex items-center gap-1">
            <HelpCircle className="w-3 h-3" /> FAQ
          </Link>
        </div>
      </div>
      
      <div className="border-t border-sauna-beige/20 mt-8 pt-8 text-center text-sm text-sauna-beige/60">
        <p>© {new Date().getFullYear()} SaunaDip • Built in Ireland 🇮🇪</p>
      </div>
    </footer>
  );
};

export default FooterVariant; 